import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  viewChild,
} from '@angular/core';
import { Modal } from 'flowbite';
import moment from 'moment';
import { IActivePass, IBasicTemporalPass } from 'src/app/models';
import { ActivePassesService, TemporalPassesService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

@Component({
  selector: 'app-modal-active-passes',
  templateUrl: './modal-active-passes.component.html',
  styleUrl: './modal-active-passes.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalActivePassesComponent implements OnChanges {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() hidden = true;
  /** The regular user whose active passes we manage. */
  @Input() userId: number | undefined;
  /** Client IDs that BOTH the displayed user and the logged-in user share. */
  @Input() allowedClientIds: number[] = [];
  @Output() changed = new EventEmitter<void>();
  readonly modalContainer = viewChild.required<ElementRef<HTMLElement>>('modalContainer');

  public images = images;

  public passes: IActivePass[] = [];
  public visiblePasses: IActivePass[] = [];

  public availableTemporalPasses: IBasicTemporalPass[] = [];
  public visibleTemporalPasses: IBasicTemporalPass[] = [];

  public loading = false;
  public mode: 'list' | 'create' | 'edit' = 'list';
  public selectedTemporalPassId: number | undefined;
  public editing: IActivePass | undefined;
  public editingEndDateTime = '';
  public editingUsages: number | undefined;
  public confirmingDelete = false;

  constructor(
    public literalService: LiteralService,
    private activePassesService: ActivePassesService,
    private temporalPassesService: TemporalPassesService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hidden'] && !this.hidden && this.userId) {
      this.refresh();
    }
    if (changes['allowedClientIds']) {
      this.applyClientFilter();
    }
  }

  async refresh(): Promise<void> {
    if (!this.userId) return;
    this.loading = true;
    try {
      const [passes, temporal] = await Promise.all([
        this.activePassesService.getByUser(this.userId),
        // Page 0 / size 200 so all relevant temporal passes load in one shot.
        this.temporalPassesService.getTemporalPasses(0, 200),
      ]);
      this.passes = passes;
      this.availableTemporalPasses = temporal;
      this.applyClientFilter();
    } finally {
      this.loading = false;
    }
  }

  private applyClientFilter(): void {
    const allowed = new Set(this.allowedClientIds);
    this.visiblePasses = this.passes.filter(
      (p) => p.clientId !== undefined && allowed.has(p.clientId),
    );
    this.visibleTemporalPasses = this.availableTemporalPasses.filter(
      (t) =>
        this.getTemporalPassClientId(t) !== undefined &&
        allowed.has(this.getTemporalPassClientId(t)!),
    );
  }

  private getTemporalPassClientId(
    pass: IBasicTemporalPass,
  ): number | undefined {
    return (pass.client as any)?.id ?? (pass as any).clientId;
  }

  getTemporalPassesForSelect(): any[] {
    return this.visibleTemporalPasses.map((t) => ({
      id: t.id,
      value: t.id,
      label: t.name,
    }));
  }

  formatDate(value: string | undefined): string {
    if (!value)
      return this.literalService.get('users.activePasses.noExpiry', true);
    const m = moment(value);
    return m.isValid()
      ? m.format('DD/MM/YYYY HH:mm')
      : this.literalService.get('users.activePasses.noExpiry', true);
  }

  startCreate(): void {
    this.mode = 'create';
    this.selectedTemporalPassId = undefined;
    this.editing = undefined;
  }

  startEdit(pass: IActivePass): void {
    this.mode = 'edit';
    this.editing = pass;
    this.editingEndDateTime = pass.endDateTime
      ? moment(pass.endDateTime).format('YYYY-MM-DDTHH:mm')
      : '';
    this.editingUsages = pass.usages;
    this.confirmingDelete = false;
  }

  cancel(): void {
    this.mode = 'list';
    this.selectedTemporalPassId = undefined;
    this.editing = undefined;
    this.confirmingDelete = false;
  }

  requestDelete(): void {
    this.confirmingDelete = true;
  }

  cancelDelete(): void {
    this.confirmingDelete = false;
  }

  async confirmDelete(): Promise<void> {
    if (!this.editing) return;
    this.loading = true;
    try {
      await this.activePassesService.deleteActivePass(this.editing.id);
      this.changed.emit();
      this.confirmingDelete = false;
      this.mode = 'list';
      this.editing = undefined;
      await this.refresh();
    } finally {
      this.loading = false;
    }
  }

  selectTemporalPass(event: any): void {
    const value = event.options ? event.options[0]?.id : event.target?.value;
    this.selectedTemporalPassId = value ? Number(value) : undefined;
  }

  updateEndDateTime(event: any): void {
    this.editingEndDateTime = event.target?.value ?? '';
  }

  updateUsages(event: any): void {
    const value = event.target?.value ?? '';
    this.editingUsages = value === '' ? undefined : Number(value);
  }

  canSaveCreate(): boolean {
    return !!this.selectedTemporalPassId && !!this.userId;
  }

  canSaveEdit(): boolean {
    return !!this.editing && !!this.editingEndDateTime;
  }

  async create(): Promise<void> {
    if (!this.canSaveCreate()) return;
    this.loading = true;
    try {
      await this.activePassesService.createActivePass({
        temporalPassId: this.selectedTemporalPassId!,
        userId: this.userId!,
      });
      this.changed.emit();
      this.mode = 'list';
      this.selectedTemporalPassId = undefined;
      await this.refresh();
    } finally {
      this.loading = false;
    }
  }

  async save(): Promise<void> {
    if (!this.canSaveEdit() || !this.editing) return;
    this.loading = true;
    try {
      await this.activePassesService.updateActivePass(this.editing.id, {
        endDateTime: moment(this.editingEndDateTime).format(
          'YYYY-MM-DDTHH:mm:ss',
        ),
        usages: this.editingUsages,
      });
      this.changed.emit();
      this.mode = 'list';
      this.editing = undefined;
      await this.refresh();
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this.mode = 'list';
    this.selectedTemporalPassId = undefined;
    this.editing = undefined;
    this.confirmingDelete = false;
    this.modal.hide();
  }
}
